import React, { useEffect, useState } from 'react';
import Loading from '../../components/loading/Loading';
import { BackendPayload, FormsProps, FormType, QuizQuestion } from '../../types/forms';
import { getDecisionCriteria } from '../../api/decisions.api';
import { transformFormFields } from '../../utils/forms.utils';
import ViewTableForm from '../../components/forms/ViewTableForm';
import ViewSingleRowForm from '../../components/forms/ViewSingleRowForm';
import ViewAndEditSingleRowForm from '../../components/forms/ViewAndEditSingleRowForm';
import AddSingleRowForm from '../../components/forms/AddSingleRowForm';
import ViewAndEditTableForm from '../../components/forms/ViewAndEditTableForm';
import ViewAndEditUserTable from '../../components/forms/ViewAndEditUserTable';
import ViewUserTable from '../../components/forms/ViewUserTable';
import QuizForm from '../../components/forms/QuizForm';
import { useEvents } from '../../hooks/useEvents';
import { IframeMapping } from '../../types/mashups';
import { useAppConfig } from '../../context/app-configs-context';
import { useAuth } from '../../context/auth-context';

const components = {
    add_single_row: AddSingleRowForm,
    view_single_row: ViewSingleRowForm,
    view_and_edit_single_row: ViewAndEditSingleRowForm,
    view_entire_table: ViewTableForm,
    view_and_edit_entire_table: ViewAndEditTableForm,
    view_combined_row: ViewAndEditTableForm,
    view_user_friendly_table: ViewUserTable,
    view_and_edit_user_friendly_table: ViewAndEditUserTable,
    branching: AddSingleRowForm,
    quiz: QuizForm,
};

const Forms = ({
    formId,
    mappings = [],
}: {
    formId: string | undefined;
    mappings?: IframeMapping[];
}) => {
    const { setAppConfigs } = useAppConfig();
    const { tokens } = useAuth();

    const [ loading, setLoading ] = useState(true);
    const [ formType, setFormType ] = useState<FormType>();
    const [ formName, setFormName ] = useState<string>('');
    const [ skin, setSkin ] = useState<'skin1' | 'skin2' | 'skin3' | 'skin4' | 'skin5'>('skin2');
    const [ linkedFormId, setLinkedFormId ] = useState<number>();
    const [ formAllFields, setFormAllFields ] = useState<BackendPayload['fields']>([]);
    const [ quizQuestions, setQuizQuestions ] = useState<QuizQuestion[]>([]);

    const { emit, subscribe } = useEvents();

    const extractQuizQuestions = (response: any): QuizQuestion[] => {
        const elements = response?.form_config?.elements;

        if (!Array.isArray(elements)) {
            return [];
        }

        const quizGroup = elements.find((el: any) => el.group === 'quiz');

        if (!quizGroup?.children) {
            return [];
        }

        return quizGroup.children.map((q: any) => ({
            id: q.id,
            question: q.question,
            type: q.type,
            options: q.options || [],
            correctAnswer: q.correctAnswer,
            required: q.required ?? false,
        }));
    };

    useEffect(() => {
        if (!formId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        getDecisionCriteria(formId)
        .then(response => {
            const { link_decision_id, config } = response;
            if (config.loginrequired && !tokens.sessionToken) {
                emit('http-401', { code: 401 });
                return;
            }

            const { form_type, skin } = config;

            setFormType(form_type || 'add_single_row');
            setFormName(response.name);
            setSkin(skin || 'skin2');
            setLinkedFormId(link_decision_id);

            if (form_type === 'quiz') {
                const qs = extractQuizQuestions(response);

                setQuizQuestions(qs);
                setFormAllFields([]);
                setAppConfigs(response.view_config);

                return;
            }

            const elements: any[] = response.form_config.elements;
            const formattedElements: BackendPayload['fields'] = [];

            elements.forEach(element => {
                formattedElements.push(
                    ...transformFormFields(element.children),
                );
            });

            setFormAllFields(formattedElements);
            setAppConfigs(response.view_config);
        })
        .catch(err => {
            if ([ 401, 404 ].includes(err?.response?.status)) {
                emit('http-401', {
                    code: err?.response?.status,
                });

                return;
            }

            console.log(err);
        })
        .finally(() => {
            setLoading(false);
        });

    }, [
        formId,
        emit,
        setAppConfigs,
        tokens.sessionToken,
    ]);

    useEffect(() => {
        const relevantMappings = mappings.filter(
            m => m.to.formID === formId,
        );

        if (!relevantMappings.length || !formAllFields.length) {
            return;
        }

        const unsubscribes = relevantMappings.map(item => {
            return subscribe(
                `REFRESH_FORMS_${item.from.formID}`,
                data => {
                    const newValue =
                        data[item.from.fieldID]?.value;

                    setFormAllFields(allFields => {
                        return allFields.map(field => {
                            if (field.id !== item.to.fieldID) {
                                return field;
                            }

                            return {
                                ...field,
                                defaultValue: newValue,
                            };
                        });
                    });
                },
            );
        });

        return () => {
            unsubscribes.forEach(unsubscribe =>
                unsubscribe(),
            );
        };

    }, [
        mappings,
        subscribe,
        formId,
        formAllFields,
    ]);

    if (!formId || !formType) {
        return <Loading/>;
    }

    const CurrentComponent = components[formType];

    if (!CurrentComponent) {
        return <Loading/>;
    }

    const props: FormsProps = {
        formId,
        linkedFormId,
        skin,
        formAllFields,
        formType,
        formName,
        quizQuestions,
    };

    return (
        <div>
            {loading ? (
                <Loading/>
            ) : (
                <div
                    style={{
                        width: '100%',
                        margin: '16px auto 0',
                    }}
                >
                    <CurrentComponent {...props} />
                </div>
            )}
        </div>
    );
};

export default Forms;